<script setup lang="ts">
// Vue Imports
import { ref } from "vue"
import { Menu, MenuButton, Dialog, DialogPanel } from "@headlessui/vue"
import { RouterLink, useRoute } from 'vue-router'
import {deleteCookie, getCookie, setCookie} from '../../utils/cookies';
import { addUtm } from '../../utils/utmParameter';
import {corporateRegex} from "../../utils/corporateClients";

// Component Logic
const cdn = import.meta.env.VITE_CDN_MWI_BASE_URL
const route = useRoute()

const logoUrl = `${cdn}/logos/MWI-logo-2.svg`
const crossUrl = `${cdn}/icons/menu-close-cross.svg`
const menuUrl = `${cdn}/icons/teal/menu.svg`
const loginUrl = `/login`

const mobileMenuOpen = ref(false)

const regex = corporateRegex
const srcValue = ref(null);

// if there is a valid 'src' query string parameter, add it to the store
const isEntryRoute = route.path.includes('/register') || route.path.includes('/login')

if(isEntryRoute && route.query.src !== undefined) {
  if (regex.test(route.query.src.toString())) {
    setCookie('corporate-uuid', route.query.src.toString(), 30, 'days');
    console.log('corporateUuid cookie set value: ' + getCookie('corporate-uuid'));

    srcValue.value = getCookie('corporate-uuid')
  }
} else {
  deleteCookie('corporate-uuid');
}

</script>

<template>
  <header class="bg-white border-b border-lighter-grey">
    <nav
      class="flex mx-auto max-w-7xl items-center justify-between gap-8 py-6 px-8 xl:px-0"
      aria-label="Navigation"
    >
      <a
        :href="addUtm('/')"
        aria-label="Home logo button"
        :class="{'hover:cursor-auto pointer-events-none' : route.path === '/'}"
      >
        <img :src="logoUrl" alt="My Wealth invest icon" />
      </a>

      <div class="hidden lg:flex items-center gap-5 lg:gap-8">
        <a
          :href="addUtm('/global-index-service')"
          class="body-3-semi-bold text-granite hover:text-teal"
          aria-label="Global Index Service button"
        >
          Global Index Service
        </a>
        <a
          :href="addUtm('/self-select-service')"
          class="body-3-semi-bold text-granite hover:text-teal"
          aria-label="Self Select Service button"
        >
          Self-Select Service
        </a>
        <a
          :href="addUtm('/news')"
          class="body-3-semi-bold text-granite hover:text-teal"
          aria-label="News button"
        >
          News
        </a>
        <a
          :href="addUtm('/get-in-touch')"
          class="body-3-semi-bold text-granite hover:text-teal"
          aria-label="Get in touch button"
        >
          Get in touch
        </a>

        <div>
          <RouterLink
            :to="srcValue ? { path: loginUrl, query: { src: srcValue } } : { path: loginUrl }"
            aria-label="Login button"
            class="primary-mwi button-padding body-3-semi-bold rounded-full max-w-max"
          >
            Log In
          </RouterLink>
        </div>
      </div>

      <div class="flex lg:hidden">
        <Menu as="div" class="relative inline-block text-center">
          <div>
            <MenuButton
              aria-label="Open mobile hamburger menu"
              class="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 cursor-pointer"
              @click="mobileMenuOpen = true"
            >
              <span class="sr-only">Open main menu</span>
              <img :src="menuUrl" alt="Open menu icon" />
            </MenuButton>
          </div>
        </Menu>
      </div>
    </nav>

    <!-- Mobile dropdown -->
    <template>
      <transition
        enter-active-class="transition ease-out duration-1000"
        enter-from-class="opacity-0 scale-y-0 origin-top"
        enter-to-class="opacity-100 scale-y-100 origin-top"
        leave-active-class="transition ease-in duration-900"
        leave-from-class="opacity-100 scale-y-100 origin-top"
        leave-to-class="opacity-0 scale-y-0 origin-top"
      >
        <Dialog class="lg:hidden" @close="mobileMenuOpen = false" :open="mobileMenuOpen">
          <div class="fixed inset-0 z-50" />
          <DialogPanel class="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white shadow-lg sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
            <div class="flex items-center justify-between p-8">
              <a
                :href="addUtm('/')"
                aria-label="Home logo button"
                @click="trackHeaderLinkClick('Logo')"
              >
                <img
                  v-if="logoUrl"
                  :src="logoUrl"
                  alt="My Wealth invest icon"
                  height="50"
                  width="154"
                  :class="{'hover:cursor-auto' : route.path === '/'}"
                  loading="lazy"
                />
              </a>

              <button
                aria-label="Close mobile hamburger menu"
                class="inline-flex items-center justify-center cursor-pointer"
                @click="mobileMenuOpen = false"
              >
                <img
                  v-if="crossUrl"
                  :src="crossUrl"
                  alt="Close menu icon"
                  height="28"
                  width="28"
                  loading="lazy"
                />
              </button>
            </div>
            <div class="flow-root justify-between p-8">
              <div class="flex flex-col space-y-6">
                <a
                  :href="addUtm('/global-index-service')"
                  class="body-3-semi-bold text-granite hover:text-teal active:border-b-2 active:border-petal active:pb-2"
                  aria-label="Global Index Service button"
                >
                  Global Index Service
                </a>
                <a
                  :href="addUtm('/self-select-service')"
                  class="body-3-semi-bold text-granite hover:text-teal active:border-b-2 active:border-petal active:pb-2"
                  aria-label="Self Select Service button"
                >
                  Self-Select Service
                </a>
                <a
                  :href="addUtm('/news')"
                  class="body-3-semi-bold text-granite hover:text-teal active:border-b-2 active:border-petal active:pb-2"
                  aria-label="News button"
                >
                  News
                </a>
                <a
                  :href="addUtm('/get-in-touch')"
                  class="body-3-semi-bold text-granite hover:text-teal active:border-b-2 active:border-petal active:pb-2"
                  aria-label="Get in touch button"
                >
                  Get in touch
                </a>
                <router-link
                  :to="srcValue ? { path: loginUrl, query: { src: srcValue } } : { path: loginUrl }"
                  target="_blank"
                  aria-label="Login button"
                  class="mt-4 body-3-semi-bold primary-mwi button-padding rounded-full max-w-max"
                >
                  Log In
                </router-link>
              </div>
            </div>
          </DialogPanel>
        </Dialog>
      </transition>
    </template>
  </header>
</template>
